/**
 * Placeholder plugin for CKEditor 5, with CC-specific functionality.
 *
 * It displays the "placeholder" pulldown menu in the toolbar,
 * which can insert various placeholders into the text.
 * Example placeholder text is "{xxx}".
 *
 * Available placeholder presets are defined in the editor configuration
 * under the "placeholderConfig.types" key.
 *
 * Placeholder renders to HTML as a span with the "placeholder" class.
 * Optionally, it can have a "data-label" attribute with the placeholder name,
 * and a "data-code" attribute with the placeholder code, like the "{xxx}",
 * including curly brackets.
 *
 * Developed based on the CKEditor 5 "Implementing an inline widget" example.
 * @see https://ckeditor.com/docs/ckeditor5/latest/framework/tutorials/widgets/implementing-an-inline-widget.html
 * @see https://github.com/ckeditor/ckeditor5-tutorials-examples
 * Source code under `ckeditor5-tutorials-examples/inline-widget/starter-files`
 * CC modification enables "code" and "label" attributes instead of a single "name" attribute.
 */
import {
  Command,
  Plugin,
  Widget,
  toWidget,
  viewToModelPositionOutsideModelElement,
  ViewModel,
  addListToDropdown,
  createDropdown,
  Collection,
  Element
} from 'ckeditor5';
import './placeholder.css';

export default class Placeholder extends Plugin {
  static get requires() {
    return [PlaceholderEditing, PlaceholderUI];
  }
}

class PlaceholderCommand extends Command {
  execute({ label, code }) {
    const { editor } = this;
    const { selection } = editor.model.document;

    editor.model.change(writer => {
      const placeholder = writer.createElement('placeholder', {
        ...Object.fromEntries(selection.getAttributes()),
        label,
        code,
      });

      // ... and insert it into the document. Put the selection on the inserted element.
      editor.model.insertObject(placeholder, null, null, { setSelection: 'on' });
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;

    const isAllowed = model.schema.checkChild(selection.focus.parent, 'placeholder');

    this.isEnabled = isAllowed;
  }
}

class PlaceholderUI extends Plugin {
  init() {
    const editor = this.editor;
    const t = editor.t;
    const placeholderItems = editor.config.get('placeholderConfig.types');

    // The "placeholder" dropdown must be registered among the UI components of the editor
    // to be displayed in the toolbar.
    editor.ui.componentFactory.add('placeholder', locale => {
      const dropdownView = createDropdown(locale);

      // Populate the list in the dropdown with items.
      if (Array.isArray(placeholderItems) && placeholderItems.length) {
        addListToDropdown(dropdownView, getDropdownItemsDefinitions(placeholderItems));
      } else {
        // no placeholders provided
      }

      dropdownView.buttonView.set({
        // The t() function helps localize the editor. All strings enclosed in t() can be
        // translated and change when the language of the editor changes.
        label: t('Placeholder'),
        tooltip: true,
        withText: true
      });

      // Disable the placeholder button when the command is disabled.
      const command = editor.commands.get('placeholder');
      dropdownView.bind('isEnabled').to(command);

      // Execute the command when the dropdown item is clicked (executed).
      this.listenTo(dropdownView, 'execute', evt => {
        const { label, code } = evt.source;
        editor.execute('placeholder', { label, code });
        editor.editing.view.focus();
      });

      return dropdownView;
    });
  }
}

/**
 * From array of placeholder item names, return a collection of dropdown item definitions.
 * @param {array} placeholderItemNames string names only
 * @returns {Collection}
 */
function getDropdownItemsDefinitions(placeholderItemNames) {
  const itemDefinitions = new Collection();

  for (const name of placeholderItemNames) {
    const definition = {
      type: 'button',
      model: new ViewModel({
        commandParam: name,
        label: name,
        code: `{${name}}`,
      }, {
        withText: true,
      })
    };

    // Add the item definition to the collection.
    itemDefinitions.add(definition);
  }

  return itemDefinitions;
}

/**
 * Given array of objects, return a collection of dropdown item definitions.
 * @param {array} placeholderItems array of objects with "label" and "code" props
 * @returns
 */
function getDropdownItemsDefinitionsLabelCode(placeholderItems) {
  const itemDefinitions = new Collection();

  for (const item of placeholderItems) {
    const { label, code } = item;
    const definition = {
      type: 'button',
      model: new ViewModel({
        commandParam: item,
        label,
        code,
      }, {
        withText: true,
      })
    };

    // Add the item definition to the collection.
    itemDefinitions.add(definition);
  }

  return itemDefinitions;
}

class PlaceholderEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add('placeholder', new PlaceholderCommand(this.editor));

    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, viewElement => viewElement.hasClass('placeholder'))
    );
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register('placeholder', {
      // Behaves like a self-contained inline object (e.g. an inline image)
      // allowed in places where $text is allowed (e.g. in paragraphs).
      // The inline widget can have the same attributes as text (for example linkHref, bold).
      inheritAllFrom: '$inlineObject',

      allowAttributes: ['label', 'code'], // label is the pulldown menu name, code is the '{xxx}' like string inserted to the text
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: ['placeholder']
      },
      model: (viewElement, { writer: modelWriter }) => {
        // Extract the "code" and label from data- attributes
        const code = viewElement.getAttribute('data-code') ?? viewElement.getChild(0).data;
        const label = viewElement.getAttribute('data-label') ?? '-';

        return modelWriter.createElement('placeholder', { label, code });
      }
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'placeholder',
      view: (modelItem, { writer: viewWriter }) => {
        const widgetElement = createPlaceholderView(modelItem, viewWriter);

        // Enable widget handling on a placeholder element inside the editing view.
        return toWidget(widgetElement, viewWriter);
      }
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'placeholder',
      view: (modelItem, { writer: viewWriter }) => createPlaceholderView(modelItem, viewWriter)
    });

    // Helper method for both downcast converters.
    /**
     *
     * @param {Element} modelItem
     * @param {*} viewWriter
     * @returns
     */
    function createPlaceholderView(modelItem, viewWriter) {
      const code = modelItem.getAttribute('code');
      const label = modelItem.getAttribute('label');

      const placeholderView = viewWriter.createContainerElement('span', {
        class: 'placeholder',
        'data-code': code,
        'data-label': label,
      });

      // Insert the placeholder name (as a text).
      const innerText = viewWriter.createText(code); // "{xxx}"
      viewWriter.insert(viewWriter.createPositionAt(placeholderView, 0), innerText);

      return placeholderView;
    }
  }
}
